import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { AutoCompleteInput } from '@/components/atoms/AutocompleteInput/AutoCompleteInput';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import { Spinner } from '@/components/atoms/Spinner';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { AquacultureSite } from '@/domain/aquacultureSites';
import type { DomainFishType, LiceType, LocationTypeInfo } from '@/domain/cages';

import LocationTypeFields from './LocationTypeFields';
import {
    type AddNewLocationInput,
    fromLocationFormValues,
    type LocationFormInitialState,
    type LocationFormValues,
    toFieldString,
    toLocationFormValues,
} from './locationFormValues';
import TimezoneField from './TimezoneField';

import styles from './AddLocationModal.module.scss';

/** Only a named site can be an autocomplete option: the name is the option's value. */
function isNamed(site: AquacultureSite): site is AquacultureSite & { value: string } {
    return site.value !== undefined;
}

type NewOrModify =
    | { modifyOrNew: 'new'; initialState?: undefined }
    | { modifyOrNew: 'modify'; initialState: LocationFormInitialState };

type AddLocationModalProps = {
    onSubmit: (props: AddNewLocationInput) => Promise<unknown>;
    fetchAquaCultureSites: (input: string) => Promise<AquacultureSite[]>;
    locationTypesList: LocationTypeInfo[];
    fishTypesList: DomainFishType[];
    liceTypesList: LiceType[];
    onClose: () => void;
    isOpen: boolean;
} & NewOrModify;

export default function AddLocationModal(props: AddLocationModalProps) {
    const {
        onSubmit,
        onClose,
        isOpen,
        fetchAquaCultureSites,
        modifyOrNew,
        initialState,
        fishTypesList = [],
        liceTypesList = [],
        locationTypesList = [],
    } = props;

    const { t } = useTranslation('clientLocations');
    const { t: tCommon } = useTranslation('common');

    const defaultValues = toLocationFormValues(initialState);
    const allowsNoFishType = modifyOrNew === 'modify' && initialState.fishtypeid == null;
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<LocationFormValues>({ defaultValues });
    const [submitError, setSubmitError] = useState<ReactNode>(null);

    // The modal stays mounted while closed, so a failure must not greet the next opening.
    function handleClose() {
        setSubmitError(null);
        onClose();
    }

    async function submit(values: LocationFormValues) {
        setSubmitError(null);
        try {
            await onSubmit(fromLocationFormValues(values));
        } catch (error: unknown) {
            setSubmitError(apiErrorContent(error));
            return;
        }
        handleClose();
    }

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            centered={false}
            title={
                modifyOrNew === 'new'
                    ? t('addLocationModal.title')
                    : t('addLocationModal.editTitle')
            }>
            <form onSubmit={handleSubmit(submit)}>
                <ModalBody>
                    <FormField
                        label={t('addLocationModal.locationName')}
                        htmlFor="name"
                        error={errors.name ? t('addLocationModal.nameRequired') : undefined}>
                        <TextField
                            {...register('name', {
                                required: true,
                                minLength: 1,
                            })}
                            placeholder={t('addLocationModal.locationNamePlaceholder')}
                            autoComplete="off"
                        />
                    </FormField>
                    <TimezoneField
                        register={register}
                        errors={errors}
                        storedTimezone={initialState?.timezone}
                        initialTimezone={defaultValues.timezone}
                    />
                    <FormField
                        label={t('addLocationModal.searchAquacultureSiteId')}
                        htmlFor="autocompleteinput">
                        <AutoCompleteInput
                            placeholder={t('addLocationModal.aquacultureSitePlaceholder')}
                            fetchData={(input) =>
                                fetchAquaCultureSites(input).then((sites) => sites.filter(isNamed))
                            }
                            renderer={(data) => (
                                <>
                                    <span>{data.value}</span>
                                    <span className={styles.siteArea}>
                                        {data.municipality}, {data.county}
                                    </span>
                                </>
                            )}
                            onAutoCompleteSelected={(datum) => {
                                setValue('aquacultureSiteId', toFieldString(datum?.id));
                                setValue('latitude', toFieldString(datum?.latitude));
                                setValue('longitude', toFieldString(datum?.longitude));
                            }}
                        />
                    </FormField>
                    <FormField
                        label={t('addLocationModal.aquacultureSiteId')}
                        htmlFor="aquaCultureSites">
                        <TextField {...register('aquacultureSiteId', { required: false })} />
                    </FormField>
                    <FormField label={t('addLocationModal.latitude')} htmlFor="latitude">
                        <TextField {...register('latitude', { required: false })} />
                    </FormField>
                    <FormField label={t('addLocationModal.longitude')} htmlFor="longitude">
                        <TextField {...register('longitude', { required: false })} />
                    </FormField>
                    <LocationTypeFields
                        register={register}
                        errors={errors}
                        locationTypesList={locationTypesList}
                        fishTypesList={fishTypesList}
                        liceTypesList={liceTypesList}
                        allowsNoFishType={allowsNoFishType}
                    />
                    {submitError && <Alert tone="danger">{submitError}</Alert>}
                </ModalBody>
                <ModalFooter>
                    {isSubmitting ? (
                        <div className={styles.saving}>
                            <Spinner size="md" />
                        </div>
                    ) : null}
                    <Button variant="secondary" onClick={handleClose}>
                        {tCommon('actions.cancel')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {modifyOrNew === 'new'
                            ? t('addLocationModal.submitAdd')
                            : t('addLocationModal.submitUpdate')}
                    </Button>
                </ModalFooter>
            </form>
        </ModalComponent>
    );
}
